import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { User } from 'lucide-react';
import { operators, seasonMapPools } from './constants';

const FieldRenderer = ({
  fieldKey,
  row,
  value,
  activeMode,
  activePlayers,
  playerImages = {},
  matchTypes,
  vsTeams,
  structuredData,
  updateCell,
  inputRefs,
  onKeyDown,
  showAllColumns = false
}) => {
  const refKey = `${row._rowId}-${fieldKey}`;
  const ddRef = useRef(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ddRef.current &&
        !ddRef.current.contains(e.target) &&
        (!menuRef.current || !menuRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width
      });
    }
  }, [open]);

  if (fieldKey === 'Player') {
    const selectedPlayers = structuredData.map(r => r.Player).filter(Boolean);
    const options = activePlayers.filter(p => p === value || !selectedPlayers.includes(p));

    const avatar = (name) => (
      playerImages[name] ? (
        <img
          src={playerImages[name]}
          alt={name}
          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1px solid #1f2937' }}
        />
      ) : (
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '1px solid #1f2937',
            background: '#111827',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <User size={16} color="#9ca3af" />
        </span>
      )
    );

    return (
      <div ref={ddRef} style={{ position: 'relative', minWidth: 200, overflow: 'visible' }}>
        <button
          type="button"
          ref={btnRef}
          onClick={() => setOpen(!open)}
          onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
          style={{
            width: '100%',
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid #334155',
            background: '#0f172a',
            color: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer'
          }}
        >
          {avatar(value)}
          <span style={{ fontSize: 13 }}>{value || 'Select Player'}</span>
          <span style={{ marginLeft: 'auto', color: '#6b7280' }}>▼</span>
        </button>

        {open && ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{
              ...menuStyle,
              zIndex: 1200,
              background: '#0b1222',
              border: '1px solid #1f2937',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
              maxHeight: 260,
              overflowY: 'auto',
              padding: 6
            }}
          >
            {options.map((player) => (
              <button
                type="button"
                key={player}
                onClick={() => { updateCell(row._rowId, fieldKey, player); setOpen(false); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: 'none',
                  background: value === player ? '#1f2937' : 'transparent',
                  color: '#e5e7eb',
                  cursor: 'pointer'
                }}
              >
                {avatar(player)}
                <span style={{ fontSize: 13 }}>{player}</span>
              </button>
            ))}
            {!options.length && (
              <div style={{ color: '#6b7280', padding: '6px 8px', fontSize: 12 }}>No players available</div>
            )}
          </div>,
          document.body
        )}
      </div>
    );
  }

  if (fieldKey === 'Map') {
    const seasonYear = row?.Date
      ? String(new Date(row.Date).getFullYear())
      : String(new Date().getFullYear());
    const latestSeasonKey = Object.keys(seasonMapPools).sort().reverse()[0];
    const maps = (
      seasonMapPools[seasonYear]?.[activeMode] ||
      seasonMapPools[latestSeasonKey]?.[activeMode] ||
      []
    );
    return (
      <select
        ref={el => { if (el) inputRefs.current[refKey] = el; }}
        value={value || ''}
        onChange={e => updateCell(row._rowId, fieldKey, e.target.value)}
        onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
      >
        <option value="">Select Map</option>
        {maps.map((map, i) => (
          <option key={i} value={map}>{map}</option>
        ))}
      </select>
    );
  }

  if (fieldKey === 'Operator' && activeMode !== 'SND') {
    const selectedByOtherRows = structuredData
      .filter(item => item._rowId !== row._rowId)
      .map(item => item.Operator)
      .filter(Boolean);
    const availableOperators = [
      ...(value && !operators.includes(value) ? [value] : []),
      ...operators.filter(op => op === value || !selectedByOtherRows.includes(op)),
    ];

    return (
      <select
        ref={el => { if (el) inputRefs.current[refKey] = el; }}
        value={value || ''}
        onChange={e => updateCell(row._rowId, fieldKey, e.target.value)}
        onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
      >
        <option value="">Select Operator</option>
        {availableOperators.map((op, i) => (
          <option key={i} value={op}>{op}</option>
        ))}
      </select>
    );
  }

  if (fieldKey === 'MatchType') {
    return (
      <select
        ref={el => { if (el) inputRefs.current[refKey] = el; }}
        value={value || ''}
        onChange={e => updateCell(row._rowId, fieldKey, e.target.value)}
        onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
      >
        <option value="">Select Competition</option>
        {matchTypes.map((type, i) => (
          <option key={i} value={type}>{type}</option>
        ))}
      </select>
    );
  }

  if (fieldKey === 'Date') {
    return (
      <input
        type="date"
        ref={el => { if (el) inputRefs.current[refKey] = el; }}
        value={value || ''}
        onChange={e => updateCell(row._rowId, fieldKey, e.target.value)}
        onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
      />
    );
  }

  const numberFields = ['Score', 'K', 'D', 'A', 'HillTime', 'Impact', 'TotalDamage', 'CaptureKills', 'DistanceMoved', 'OperatorValue', 'ADR', 'FirstBloods', 'Clutches1v1', 'Damage'];
  if (numberFields.includes(fieldKey)) {
    return (
      <input
        type="number"
        ref={el => { if (el) inputRefs.current[refKey] = el; }}
        value={value || ''}
        onChange={e => updateCell(row._rowId, fieldKey, e.target.value)}
        onKeyDown={e => onKeyDown?.(e, fieldKey, row)}
        placeholder={fieldKey}
      />
    );
  }

  return null;
};

export default FieldRenderer;
